import React, { useRef, useState } from 'react';

/**
 * Clickable + drag-and-droppable image slot for a floor card.
 *
 * Users can either click the zone (which opens the native file picker
 * via a hidden <input type="file">) or drop an image file onto it.
 * While a file is being dragged over, the zone highlights and shows a
 * "drop to upload/replace" overlay so it's obvious where to drop.
 *
 * Only the first file from a drop is used, and it must have an
 * image/* MIME type — other drops are ignored (not treated as errors
 * to keep the interaction forgiving).
 */
const FloorImageDropZone = ({
  preview,
  emptyLabel,
  ariaLabel,
  onFileSelected,
}) => {
  const inputRef = useRef(null);
  const [isOver, setIsOver] = useState(false);
  // dragenter/dragleave fire for every child element the cursor crosses,
  // which causes the "is-over" class to flicker. Counting enter/leave
  // pairs gives a stable hover state.
  const dragDepthRef = useRef(0);

  const openPicker = () => inputRef.current?.click();

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const hasImageFile = (e) => {
    const items = e.dataTransfer?.items;
    if (!items || items.length === 0) return true;
    return Array.from(items).some(
      (i) => i.kind === 'file' && (!i.type || i.type.startsWith('image/')),
    );
  };

  const onDragEnter = (e) => {
    if (!hasImageFile(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsOver(true);
  };

  const onDragOver = (e) => {
    if (!hasImageFile(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.type && !file.type.startsWith('image/')) return;
    onFileSelected(file);
  };

  const onChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    e.target.value = '';
  };

  return (
    <div
      className={`wizard-floor-card-image wizard-floor-dropzone ${
        isOver ? 'is-over' : ''
      } ${preview ? 'has-image' : 'is-empty'}`}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={openPicker}
      onKeyDown={onKeyDown}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {preview ? (
        <img src={preview} alt={ariaLabel} />
      ) : (
        <div className="wizard-floor-image-empty">{emptyLabel}</div>
      )}
      <div className="wizard-floor-dropzone-overlay" aria-hidden>
        <span className="material-symbols-outlined">upload</span>
        <span>{preview ? 'Drop to replace' : 'Drop to upload'}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="wizard-floor-dropzone-input"
        onChange={onChange}
        tabIndex={-1}
      />
    </div>
  );
};

export default FloorImageDropZone;
